import { useRouter } from 'next/router'
import { ReactNode, createContext, useCallback, useMemo, useState } from 'react'
import { ICheckoutContext } from 'types/contexts'
import { InventoryItem, Venue, VenueEvent } from 'types/model'
import { Booking, BookingCreateRequest } from 'types/model/booking'

import { useOpenState } from '@hooks/open-state'
import { useSession } from '@hooks/session'
import useRequest from '@hooks/request'
import { useQueryParams } from '@hooks/query-params'
import { clearCart } from '@utils/cart'
import { getId, stripPhoneNumber } from '@utils/strings'
import { env } from '@lib/env'

import { bookingApi, useBooking, useBookingTickets } from '@modules/booking/data'
import { useInventory } from '@modules/inventory/data'
import { widgetEventManager } from '@modules/widget/events'
import { CheckoutFormValues } from 'types/model/checkout'
import { analytics } from '@lib/analytics'
import { TrackEvent } from '@utils/analytics/types'
import { utcToISO, withDateFormat } from '@utils/date-time'

export const checkoutContext = createContext<ICheckoutContext>({} as ICheckoutContext)

export function CheckoutContext({
  children,
  venue,
  event,
  affiliateId,
  attributionId,
  initialValues,
  embeddedWidget = false,
}: {
  children: ReactNode
  venue: Venue
  event?: VenueEvent | null
  affiliateId?: string | null
  attributionId?: string | null
  initialValues?: Partial<ICheckoutContext>
  embeddedWidget?: boolean
}) {
  /* related contexts and env states */
  const router = useRouter()
  const session = useSession()
  const inventoryModal = useOpenState()
  const checkoutModal = useOpenState()
  const { theme, iframe } = useQueryParams()

  /* Changeable state management */
  const [bookingId, setBookingId] = useState<string | undefined | null>(initialValues?.bookingId)
  const [selectedInventory, setSelectedInventory] = useState<InventoryItem>()
  const [fallbackBooking, setFallbackBooking] = useState<Booking | undefined>(
    initialValues?.booking
  )
  const [quantity, setQuantity] = useState<number>(fallbackBooking?.quantity ?? 1)

  /* Data Hooks for the booking, tickets, and the inventory item */

  const {
    data: booking,
    loading: bookingLoading,
    mutate: mutateBooking,
  } = useBooking(bookingId, {
    config: { fallbackData: fallbackBooking },
  })
  const { data: tickets, loading: ticketsLoading } = useBookingTickets(bookingId, {
    pause: !booking?.hasTickets,
    config: { fallbackData: initialValues?.tickets },
  })
  const { data: inventory } = useInventory(
    venue ? getId(venue) : null,
    booking?.inventory ?? null,
    { config: { fallbackData: initialValues?.inventory } }
  )

  /* Callbacks and actions to update the state and hook data */

  const setBooking = useCallback(
    (val: Booking) => {
      mutateBooking(val)
      setFallbackBooking(val)
      setBookingId(val.id)
    },
    [mutateBooking]
  )

  const selectInventory = useCallback(
    (inv: InventoryItem) => {
      setSelectedInventory(inv)
      inventoryModal.open()

      analytics.trackEvent({
        event: TrackEvent.ViewedItem,
        venue,
        eventData: {
          date: utcToISO(inv.date),
          dateFormatted: withDateFormat(inv.date, 'MM/dd/yyyy', 'UTC'),
          arrivalStartTime: inv.arrivalStartTime
            ? withDateFormat(inv.arrivalStartTime, 'h:mm a', 'UTC')
            : undefined,
          arrivalEndTime: inv.arrivalEndTime
            ? withDateFormat(inv.arrivalEndTime, 'h:mm a', 'UTC')
            : undefined,
          depositAmount: inv.depositAmount,
          type: inv.type,
          inventoryId: inv.id,
          items: [
            {
              itemName: inv.name,
              itemId: inv.id,
              itemPrice: inv.price,
              itemCategory: inv.type,
              itemQuantity: 1,
            },
          ],
          currency: venue.currencyCode,
        },
      })
    },
    [inventoryModal]
  )

  const getOrderPath = useCallback(
    (path: string) => {
      const query: Record<string, string> = {}
      if (iframe) query.iframe = iframe
      if (theme) query.theme = theme
      const pathname = `${embeddedWidget ? '/w' : ''}${path}`
      return { pathname, query }
    },
    [embeddedWidget, iframe, theme]
  )

  const beginCheckout = useCallback(
    async ({
      inventoryId,
      quantity: qty,
      arrivalTime,
      client,
      eventId,
    }: {
      inventoryId: string
      quantity: number
      arrivalTime?: number | null
      client?: 'web-fast-pass' | 'widget'
      eventId?: string
    }) => {
      const bookingRequest: BookingCreateRequest = {
        affiliateId: affiliateId ?? null,
        attributionId: attributionId ?? null,
        items: [{ inventoryId, quantity: qty }],
        payment: null,
        phone: session.user?.phone ? stripPhoneNumber(session.user.phone) : undefined,
        pickUpType: null,
        status: 'OPEN',
        trackingId: null,
        type: 'USER',
        arrivalTime,
        client,
        eventId,
      }
      const response = await bookingApi.createBooking(bookingRequest)

      setBooking(response)
      setQuantity(response.quantity)

      await router.push(getOrderPath(`/o/${response.id}`))

      analytics.trackEvent({
        event: TrackEvent.AddedItem,
        venue: venue,
        eventData: {
          date: utcToISO(response.date),
          dateFormatted: withDateFormat(response.date, 'MM/dd/yyyy', 'UTC'),
          arrivalTime: arrivalTime ? withDateFormat(arrivalTime, 'h:mm a', 'UTC') : undefined,
          dueOnArrival: response.dueOnArrival,
          total: response.total,
          type: 'TABLE',
          items: [
            {
              itemName: response.location,
              itemId: inventoryId,
              itemQuantity: qty,
            },
          ],
          currency: venue.currencyCode,
        },
      })

      inventoryModal.close()
    },
    [
      affiliateId,
      attributionId,
      getOrderPath,
      inventoryModal,
      router,
      session.user?.phone,
      setBooking,
    ]
  )

  const changeQuantity = useCallback(
    async (qty: number | null | undefined) => {
      setQuantity(qty ?? 0)
      const bookingItem = booking?.items[0]
      if (!booking || !bookingItem || !qty) return
      const bookingRequest: BookingCreateRequest = {
        affiliateId: affiliateId ?? booking.affiliate ?? null,
        attributionId: attributionId ?? booking.attributionId ?? null,
        items: [
          {
            inventoryId: bookingItem.inventory.id as string,
            quantity: qty,
          },
        ],
        payment: null,
        phone: session.user?.phone,
        pickUpType: null,
        status: 'OPEN',
        trackingId: null,
        type: 'USER',
      }
      const newBooking = await bookingApi.createBooking(bookingRequest)
      setBooking(newBooking)
      return newBooking
    },
    [affiliateId, attributionId, booking, session.user, setBooking]
  )

  const [checkout, checkingOut] = useRequest(
    async ({
      arrivalTime,
      firstName,
      lastName,
      email,
      phone,
      birthday,
      marketingOptIn,
      userNotes,
    }: CheckoutFormValues) => {
      if (!venue) throw new Error('Venue not found')
      if (!booking) throw new Error('Booking not found')
      if (!inventory) throw new Error('Inventory not found')
      if ((booking.quantity ?? 0) < 1) throw new Error('Cannot checkout with less than 1 item')
      const payment = booking.payment

      const profileUpdates = {
        firstName,
        lastName,
        email,
        phone,
        birthday: new Date(birthday).valueOf(),
        marketingOptIn,
      }
      const profileResponse = await session.updateProfile(profileUpdates)

      const bookingUpdates = {
        arrivalTime,
        customerMarketingOptIn: marketingOptIn,
        userId: session.loggedIn && !booking.user ? profileResponse?.id : undefined,
        userNotes: userNotes ? `CUSTOMER NOTES: ${userNotes}` : undefined,
      }

      // update booking and profile
      const updatedBooking = await bookingApi
        .update(booking.id, bookingUpdates)
        .catch(async (err) => {
          // There's a case where this booking link was shared and the current user does not "own" this booking.
          // In this case we duplicate the booking so that it will be owned by the current user.
          return bookingApi.duplicateBooking(booking, session?.user, bookingUpdates).catch(() => {
            throw err
          })
        })

      // send tracking data
      analytics.trackCheckout({
        ...profileUpdates,
        ...bookingUpdates,
        value: (booking.total ?? 0).toString(),
        currency: venue.currencyCode,
        birthday,
        bookingId: updatedBooking.id,
        venueId: venue.id,
        eventId: event?.id,
      })

      analytics.trackEvent({
        event: TrackEvent.StartedCheckout,
        venue,
        eventData: {
          items: [
            {
              itemName: booking.location,
              itemId: booking.id,
              itemPrice: booking.price,
              itemCategory: booking.inventoryType,
              itemQuantity: 1,
            },
          ],
          currency: venue.currencyCode,
          total: booking.total,
          tax: booking.tax,
          purchaseId: booking.id,
          // promoCode: booking.promoCode,
        },
      })

      setFallbackBooking(updatedBooking)
      clearCart()

      const { pathname, query } = getOrderPath(`/o/${updatedBooking.id}/complete`)
      const queryString = new URLSearchParams(query).toString()

      if (payment === 'NONE') {
        // no payment required, just complete the order
        await bookingApi.complete(updatedBooking.id)
        router.push({ pathname, query })
      } else {
        // create checkout session, go to stripe to process payment
        const checkoutResponse = await bookingApi.beginCheckoutSession(updatedBooking.id, {
          cancelUrl: `${env.domain}/o/${updatedBooking.id}?${queryString}`,
          successUrl: `${env.domain}/o/${updatedBooking.id}/complete?${queryString}`,
        })
        if (embeddedWidget) {
          widgetEventManager.emitStripeCheckoutRedirect(iframe, checkoutResponse.url)
        } else {
          window.location = checkoutResponse.url as unknown as Location
        }
      }
    },
    [booking, embeddedWidget, event?.id, getOrderPath, iframe, inventory, router, session, venue]
  )

  const contextValue = useMemo<ICheckoutContext>(
    () => ({
      embeddedWidget,
      selectedInventory,
      quantity,
      bookingId,
      setBooking,
      booking,
      bookingLoading,
      inventory,
      tickets,
      ticketsLoading,
      selectInventory,
      changeQuantity,
      beginCheckout,
      checkout,
      checkingOut,
      inventoryModal,
      checkoutModal,
    }),
    [
      embeddedWidget,
      selectedInventory,
      quantity,
      bookingId,
      setBooking,
      booking,
      bookingLoading,
      inventory,
      tickets,
      ticketsLoading,
      selectInventory,
      changeQuantity,
      beginCheckout,
      checkout,
      checkingOut,
      inventoryModal,
      checkoutModal,
    ]
  )

  return <checkoutContext.Provider value={contextValue}>{children}</checkoutContext.Provider>
}
